import { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import _ from "underscore";
import { Tooltip, Whisper } from "rsuite";
import pluralize from "pluralize";
import dayjs from "dayjs";

import useUpdateRecord from "api/api-hooks/records/useUpdateRecord";
import useUpdateRecordDetailLayout from "api/api-hooks/modules/useUpdateRecordDetailLayout";
import AppSpinner from "../../elements/AppSpinner";

export const StageProgressWidget = ({
  section,
  record,
  module,
  detailViewLayoutID,
}) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [completed, setCompleted] = useState(false);
  const { ProgressField, Stages } = section;
  const recordID = module.RecordID;
  const { updateRecord, recordUpdating, updateRecordError } =
    useUpdateRecord(true);
  const { updateRecordDetailLayout, updatingRecordDetailLayout } =
    useUpdateRecordDetailLayout();

  const updateCompleteStage = (isStageComplete) => {
    let layouts = module?.Templates?.RecordDetailView?.Layouts;

    layouts = Array.isArray(layouts) ? layouts : [];

    const layout = layouts.find((l) => l?.id === detailViewLayoutID);

    if (!layout) return;

    const sections = layout.Sections;

    const foundSection = _.findWhere(sections, { id: section.id });

    foundSection.CompleteStage = isStageComplete;

    // TODO: Create API Endpoint to update Section.CompleteStage field
    updateRecordDetailLayout({
      moduleAPIName: module.APIName,
      layoutSections: sections,
    });
  };

  const field = useMemo(() => {
    const field = _.findWhere(module.Fields, { APIName: ProgressField });

    return field ? field : null;
  }, [ProgressField, module]);

  const sinceTimeString = useMemo(() => {
    if (
      !record?._LastUpdatedFields?.hasOwnProperty(field?.APIName) ||
      !record._LastUpdatedFields[field?.APIName]?.LastUpdated
    ) {
      return "";
    }

    let lastUpdated = dayjs(
      record._LastUpdatedFields[field?.APIName]?.LastUpdated
    );

    return lastUpdated.fromNow(true);
  }, [record, field]);

  const sinceDateString = useMemo(() => {
    if (
      !record?._LastUpdatedFields?.hasOwnProperty(field?.APIName) ||
      !record._LastUpdatedFields[field?.APIName]?.LastUpdated
    ) {
      return "";
    }

    let lastUpdated = dayjs(
      record._LastUpdatedFields[field?.APIName]?.LastUpdated
    );

    return lastUpdated.format("MMM DD, YYYY h:mm A");
  }, [record, field]);

  useEffect(() => {
    const stages = Stages.map((s) => s.Stage);

    if (!stages?.includes(record[ProgressField])) {
      setCurrentIdx(-1);
      setCompleted(false);
    }
  }, [ProgressField, Stages, record]);

  useEffect(() => {
    Stages?.forEach((stage, i) => {
      if (record[ProgressField] !== stage.Stage) return;

      setCurrentIdx(i);
      setCompleted(i + 1 === Stages.length);
    });
  }, [record, Stages, section, ProgressField]);

  function handleChangeStage(stage, i) {
    // Handle final stage
    if (i === Stages.length - 1) {
      handleCompleteStage();
    } else {
      if (section.CompleteStage === true) {
        updateCompleteStage(false);
      }
    }

    setCurrentIdx(i);

    for (const trackedField of module.Fields) {
      if (ProgressField === trackedField.APIName) {
        const field = trackedField;

        updateRecord({
          field,
          recordID,
          moduleAPIName: module.APIName,
          payload: { [field.APIName]: stage ? stage : null },
        });
      }
    }
  }

  function handleCompleteStage() {
    setCurrentIdx(Stages.length - 1);
    setCompleted(true);

    if (section.CompleteStage === false) {
      updateCompleteStage(true);
    }

    for (const trackedField of module?.Fields ?? []) {
      if (ProgressField === trackedField?.APIName) {
        const field = trackedField;

        updateRecord({
          field,
          recordID,
          moduleAPIName: module.APIName,
          payload: {
            [field.APIName]: section.Stages[section.Stages.length - 1].Stage
              ? section.Stages[section.Stages.length - 1].Stage
              : null,
          },
        });
      }
    }
  }

  const fieldIsTracked = useMemo(() => {
    const stages = Stages.map((s) => s.Stage);
    const value = record.hasOwnProperty(field?.APIName)
      ? record[field?.APIName]
      : null;

    if (value === null) {
      return true;
    }

    return stages?.includes(value);
  }, [Stages, record, field]);

  const getStageName = useCallback(
    (stage) => {
      if (!field) {
        return stage ?? "--";
      }

      const sourceStage = _.findWhere(field?.SourcesList, {
        Value: stage,
      });

      if (!sourceStage) {
        return stage ?? "--";
      }

      return sourceStage?.Name ?? stage;
    },
    [field]
  );

  return (
    <Row className="align-items-center">
      <Col xs="12">
        <div className="progress-widget-container">
          <ul className="progress-widget d-flex flex-row flex-wrap pr-3">
            {section?.Stages?.map((stage, i) => {
              return (
                <Whisper
                  speaker={
                    <Tooltip>
                      {!field?.ReadOnly && (
                        <p className="m-0" style={{ fontSize: 14 }}>
                          {currentIdx === i
                            ? `Current ${field?.Name}`
                            : `Set ${field?.Name} to`}
                          :
                        </p>
                      )}
                      <p className="m-0" style={{ fontSize: 14 }}>
                        <span className="font-weight-bold">
                          {getStageName(stage?.Stage)}
                        </span>
                      </p>
                    </Tooltip>
                  }
                  key={stage?.Stage ?? i}
                  controlId={stage?.Stage ?? i}
                  placement="autoVertical"
                  trigger="hover"
                >
                  <li
                    style={{
                      width: `calc((100% / ${section?.Stages?.length}) - 2%)`,
                    }}
                    className={` flex-fill font-weight-500 ${
                      record[ProgressField] === stage.Stage ||
                      completed ||
                      currentIdx > i
                        ? "progress-widget-active"
                        : ""
                    } ${currentIdx === i ? "progress-widget-current" : ""}`}
                    onClick={() => {
                      if (field?.ReadOnly) {
                        return;
                      }

                      if (currentIdx === i) {
                        return;
                      }

                      handleChangeStage(stage.Stage, i);
                    }}
                  >
                    {currentIdx > i ? (
                      <>
                        <i
                          className="mdi mdi-check-circle"
                          style={{ fontSize: 22, position: "relative", top: 1 }}
                        />
                      </>
                    ) : (
                      <>
                        {currentIdx === i && recordUpdating ? (
                          <div>
                            <AppSpinner
                              style={{ position: "relative", top: -1 }}
                              size="sm"
                            />
                          </div>
                        ) : (
                          <div className="text-truncate" style={{ height: 15 }}>
                            {getStageName(stage.Stage)}
                          </div>
                        )}
                      </>
                    )}
                  </li>
                </Whisper>
              );
            })}
          </ul>
        </div>
      </Col>
      {fieldIsTracked && (
        <>
          <Col xs="12" md="">
            <div
              className={completed ? "" : ""}
              style={{ minHeight: 32, paddingTop: 3 }}
            >
              <small>
                {completed ? (
                  <>
                    <i className="mdi mdi-check-circle mr-1 text-success"></i>
                    All {currentIdx + 1}&nbsp;
                    {pluralize(field?.Name ?? "")} Completed
                    {/*{sinceDateString && ` on ${sinceDateString}`} */}
                  </>
                ) : (
                  <>
                    {!record?.hasOwnProperty(field?.APIName) ||
                    !record[field?.APIName] ? (
                      <>
                        <i className="mdi mdi-circle-empty mr-1"></i>
                        {pluralize(field?.Name ?? "")} Not Started
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-circle-half-full mr-1"></i>
                        {pluralize(field?.Name, 1 ?? "")} {currentIdx + 1} of{" "}
                        {Stages?.length} In Progress
                        {/*
                      {sinceTimeString ? (
                          <>
                            {" "}
                            for{" "}
                            <Whisper
                              placement="auto"
                              trigger="hover"
                              delayOpen={400}
                              speaker={<Tooltip>{sinceDateString}</Tooltip>}
                            >
                              <span className="cursor-pointer">
                                {sinceTimeString}
                              </span>
                            </Whisper>
                          </>
                        ) : (
                          ""
                        )}  
                    */}
                      </>
                    )}
                  </>
                )}
              </small>
            </div>
          </Col>

          {!completed && !field?.ReadOnly && Stages.length !== 0 && (
            <Col xs="12" md="auto" className="mt-3 mt-md-0">
              <Button
                size="sm"
                color="secondary"
                outline
                onClick={() => handleCompleteStage()}
              >
                Mark As Complete
              </Button>
            </Col>
          )}
        </>
      )}
      <Col xs="12">
        {!fieldIsTracked && (
          <div>
            <small>
              <i className="mdi mdi-alert-circle mr-1 text-warning"></i>The{" "}
              {pluralize(field?.Name ?? "", 1)}:{" "}
              <strong>{getStageName(record[field?.APIName])}</strong> isn't
              being tracked
            </small>
          </div>
        )}
      </Col>
    </Row>
  );
};
